import { ArrowRightLeft, RotateCcw } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRuntimeConfig } from '../../config/runtime';
import {
  APPLICATION_ROOT_PATH,
  PARTNER_INBOX_PATH,
  getApplicationStepPath,
  isPreLoginStep1,
} from '../applicationFlow';
import { getPartnerDestination } from '../../data/mocks/mockCredentials';
import { useFormState } from '../../hooks/useFormState';
import { useI18n } from '../../i18n';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

export function RoleSwitcher() {
  const { state, setRole, setWalletProvider, resetDemo } = useFormState();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const appVariant = getRuntimeConfig().app.variant;
  const isMockOnlyVariant = appVariant === 'english' || appVariant === 'oslo-circular-fast-track';
  const isCircularFastTrack = appVariant === 'oslo-circular-fast-track';
  const shouldHideForPreLoginStep1 = isPreLoginStep1(location.pathname, state);

  if (!location.pathname.startsWith(APPLICATION_ROOT_PATH) || shouldHideForPreLoginStep1) return null;

  const isKommune = state.activeRole === 'kommune';
  const isMockWallet = state.walletProvider === 'mock';
  const partnerName =
    state.step1.samarbeidspartnerNavn ||
    state.step2.partner.navn ||
    t('roleSwitcher.partnerFallback');
  const municipalityActorName = state.loggedInUser
    ? `${state.loggedInUser.name} (${state.loggedInUser.city})`
    : t('roleSwitcher.municipalityName');

  const handleReset = () => {
    resetDemo();
    navigate(isCircularFastTrack ? '/' : APPLICATION_ROOT_PATH);
  };

  const handleSwitch = () => {
    if (isKommune) {
      setRole('partner');
      if (!state.partnerHasSeenInbox) {
        navigate(PARTNER_INBOX_PATH);
      } else {
        navigate(getPartnerDestination(state));
      }
    } else {
      setRole('kommune');
      navigate(state.returnedToKommune ? getApplicationStepPath(4) ?? APPLICATION_ROOT_PATH : APPLICATION_ROOT_PATH);
    }
  };

  const handleWalletProviderChange = (checked: boolean) => {
    setWalletProvider(checked ? 'mock' : 'symfoni');
  };

  return (
    <div
      className={`w-full py-3 px-4 ${
        isKommune
          ? 'bg-oslo-blue-dark text-white'
          : 'bg-oslo-teal text-white'
      }`}
    >
      <div className="max-w-3xl mx-auto flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm">
          {t('roleSwitcher.actingAs')}{' '}
          <span className="font-bold">
            {isKommune ? municipalityActorName : partnerName}
          </span>
        </p>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {!isMockOnlyVariant && (
            <div className="flex items-center gap-2 rounded-md border border-white/30 px-2 py-1">
              <label htmlFor="wallet-provider-switch" className="text-xs">
                {t('roleSwitcher.wallet')}
              </label>
              <span className={`text-xs ${isMockWallet ? 'text-white/70' : 'font-semibold'}`}>
                Symfoni
              </span>
              <Switch
                id="wallet-provider-switch"
                checked={isMockWallet}
                onCheckedChange={handleWalletProviderChange}
                aria-label={t('roleSwitcher.walletSwitchAriaLabel')}
                className="data-[state=checked]:bg-oslo-teal data-[state=unchecked]:bg-oslo-blue-primary"
              />
              <span className={`text-xs ${isMockWallet ? 'font-semibold' : 'text-white/70'}`}>
                Mock
              </span>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="text-xs border-white/40 text-white hover:bg-white/10 hover:text-white focus-visible:text-white bg-transparent"
          >
            <RotateCcw className="w-3 h-3 mr-1.5" />
            {t('roleSwitcher.resetDemo')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSwitch}
            className="text-xs border-white/40 text-white hover:bg-white/10 hover:text-white focus-visible:text-white bg-transparent"
          >
            <ArrowRightLeft className="w-3 h-3 mr-1.5" />
            {isKommune
              ? t('roleSwitcher.switchToPartner', { partnerName })
              : t('roleSwitcher.switchToMunicipality', {
                  municipalityName: municipalityActorName,
                })}
          </Button>
        </div>
      </div>
    </div>
  );
}
